// ── Recordings section (Wave 9, REAL own-scoped read; dark lane → honest) ───
// Own available recordings for the participant's OWN session(s). The recording
// lane is DARK (mock + provider-gated) → honest unavailable/empty, never
// fabricated. Passcode is PRESENCE-ONLY (passcodePresent) — the raw passcode and
// the secure URL are NEVER rendered (the backend returns presence only; we
// render presence only).
//
// ISOLATION (FR-PDASH-001): recordings are read for the participant's OWN
// session (resolved from the OWN cohort's sessions, re-filtered to the own-cohort
// set) — never a peer session, never another cohort's recording. REPORT-EXCLUSION
// (FR-REPORT-001): a recording is not a report; no report content appears here.

import {  useMemo, useState } from "react";
import { CollectionPagination, CollectionToolbar, DataTableV2, IconCalendar } from "@vjal/ui";

import { useHonestQuery } from "../../components/honest-state";
import type {
  CoachingCohortDto,
  CoachingSessionDto,
  ListResponse,
} from "../../services/coaching-dtos";
import type { CoachingSessionRecordingsDto } from "../../services/coaching-z6-dtos";
import { ownCohortIdSet, ownSessionsOnly } from "../../domain/participant-scope";
import { ParticipantSectionShell } from "./participant-section-shell";
import {
  participantQueryKeys,
  readOwnCohorts,
  readOwnCohortSessions,
  readOwnSessionRecordings,
} from "./participant-dashboard-helpers";
import {
  mapParticipantRecordingEntries,
  useParticipantRecordingsCollection,
} from "./participant-recordings-collection";


export function ParticipantRecordingsSection() {
  const cohortsQuery = useHonestQuery<ListResponse<CoachingCohortDto>>(
    participantQueryKeys.ownCohorts(),
    readOwnCohorts
  );
  const cohorts = cohortsQuery.data?.items ?? [];
  const primaryCohortId = cohorts[0]?.id ?? "";
  const ownCohortIds = ownCohortIdSet(cohorts);

  const sessionsQuery = useHonestQuery<ListResponse<CoachingSessionDto>>(
    participantQueryKeys.cohortSessions(primaryCohortId || "missing"),
    () => readOwnCohortSessions(primaryCohortId),
    { enabled: Boolean(primaryCohortId), disabledPosture: "empty" }
  );
  const ownSessions = ownSessionsOnly(sessionsQuery.data?.items, ownCohortIds);

  const [sessionId, setSessionId] = useState<string>("");
  const activeSessionId = sessionId || ownSessions[0]?.id || "";

  const recordingsQuery = useHonestQuery<CoachingSessionRecordingsDto>(
    participantQueryKeys.recordings(activeSessionId || "missing"),
    () => readOwnSessionRecordings(activeSessionId),
    {
      enabled: Boolean(activeSessionId),
      disabledPosture: "empty",
      isEmpty: (data) => (data.recordings?.entries ?? []).length === 0,
    }
  );

  const allRows = useMemo(
    () => mapParticipantRecordingEntries(recordingsQuery.data?.recordings.entries ?? [], activeSessionId),
    [recordingsQuery.data?.recordings.entries, activeSessionId]
  );
  const collection = useParticipantRecordingsCollection(allRows, ownSessions, activeSessionId);

  const filterSessionId = collection.state.filters.session_id;
  const [prevFilterSessionId, setPrevFilterSessionId] = useState(filterSessionId);
  if (filterSessionId !== prevFilterSessionId) {
    setPrevFilterSessionId(filterSessionId);
    if (typeof filterSessionId === "string" && filterSessionId && filterSessionId !== activeSessionId) {
      setSessionId(filterSessionId);
    }
  }

  // The own-sessions read drives the picker; surface ITS posture first (no own
  // session → no recordings, honest empty, never a blank).
  if (cohortsQuery.posture !== null || sessionsQuery.posture !== null || ownSessions.length === 0) {
    const posture = cohortsQuery.posture ?? sessionsQuery.posture ?? "empty";
    return (
      <ParticipantSectionShell
        title="Recordings"
        subtitle="Recordings of your sessions (secure link behind sign-in; passcode presence only)."
        posture={posture}
        gate={cohortsQuery.gate ?? sessionsQuery.gate}
        reference={cohortsQuery.reference ?? sessionsQuery.reference}
        onRetry={cohortsQuery.posture !== null ? cohortsQuery.retry : sessionsQuery.retry}
        emptyTitle="No recordings yet"
        emptyMessage="Recordings appear here once your sessions are held and recordings are available."
        icon={<IconCalendar size={22} />}
        testId="participant-recordings-section"
      >
        <span />
      </ParticipantSectionShell>
    );
  }


  return (
    <ParticipantSectionShell
      title="Recordings"
      subtitle="Recordings of your sessions (secure link behind sign-in; passcode presence only — the raw passcode is never shown)."
      posture={recordingsQuery.posture}
      gate={recordingsQuery.gate}
      reference={recordingsQuery.reference}
      onRetry={recordingsQuery.retry}
      emptyTitle="No recordings for this session"
      emptyMessage="Recordings will appear here once they're available for the selected session."
      icon={<IconCalendar size={22} />}
      testId="participant-recordings-section"
    >
      <CollectionToolbar collection={collection} className="rounded-xl border border-line" stableChipNames />
      <div className="rounded-xl border border-line bg-white">
        <DataTableV2 collection={collection} caption="Your session recordings" />
        {collection.uiState === "ready" && <CollectionPagination collection={collection} className="px-4" />}
      </div>
    </ParticipantSectionShell>
  );
}
